import type { RebateExportCell } from './rebateExportTypes';

const CURRENCY_LEADING_DOLLAR =
  /^\$\s*\(?\s*(-)?\s*([0-9,]+(?:\.[0-9]+)?)\s*\)?$/;
const CURRENCY_MINUS_DOLLAR = /^-\s*\$\s*([0-9,]+(?:\.[0-9]+)?)$/;
const CURRENCY_WRAPPED_DOLLAR = /^\(\s*\$\s*([0-9,]+(?:\.[0-9]+)?)\s*\)$/;
const CURRENCY_DOLLAR_PARENS = /^\$\s*\(\s*([0-9,]+(?:\.[0-9]+)?)\s*\)$/;
const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;
const SLASH_DATE = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/;
const PLAIN_NUMBER = /^-?[0-9,]+(?:\.[0-9]+)?$/;
const PAREN_NUMBER = /^\(([0-9,]+(?:\.[0-9]+)?)\)$/;

function stripCommas(value: string): string {
  return value.replace(/,/g, '');
}

function toNumber(raw: string): number | null {
  const parsed = Number(stripCommas(raw));
  if (Number.isNaN(parsed)) {
    return null;
  }
  return parsed;
}

function signedAmount(
  raw: string | undefined,
  negative: boolean,
): number | null {
  const amount = toNumber(raw ?? '');
  if (amount === null) {
    return null;
  }
  return negative ? -amount : amount;
}

function matchSignedCurrency(
  raw: string,
  pattern: RegExp,
  negative: boolean,
): number | null {
  const match = raw.match(pattern);
  if (!match) {
    return null;
  }
  return signedAmount(match[1], negative);
}

export function parseCurrencyString(raw: string): number | null {
  const trimmed = raw.trim();
  return (
    matchSignedCurrency(trimmed, CURRENCY_WRAPPED_DOLLAR, true) ??
    matchSignedCurrency(trimmed, CURRENCY_MINUS_DOLLAR, true) ??
    matchSignedCurrency(trimmed, CURRENCY_DOLLAR_PARENS, true) ??
    parseLeadingDollarCurrency(trimmed)
  );
}

function parseLeadingDollarCurrency(trimmed: string): number | null {
  const leading = trimmed.match(CURRENCY_LEADING_DOLLAR);
  if (!leading) {
    return null;
  }
  return signedAmount(leading[2], leading[1] === '-' || trimmed.includes('('));
}

function utcDateFromParts(
  year: number,
  month: number,
  day: number,
): Date | null {
  const date = new Date(Date.UTC(year, month - 1, day));
  const sameCalendarDay =
    date.getUTCDate() === day &&
    date.getUTCMonth() === month - 1 &&
    date.getUTCFullYear() === year;
  return sameCalendarDay ? date : null;
}

export function parseDateString(raw: string): Date | null {
  const trimmed = raw.trim();
  const iso = trimmed.match(ISO_DATE);
  if (iso) {
    return utcDateFromParts(Number(iso[1]), Number(iso[2]), Number(iso[3]));
  }
  const slash = trimmed.match(SLASH_DATE);
  if (!slash) {
    return null;
  }
  return utcDateFromParts(Number(slash[3]), Number(slash[1]), Number(slash[2]));
}

function isLeadingZeroIdentifier(trimmed: string): boolean {
  return /^-?0\d/.test(trimmed.replace(/,/g, ''));
}

export function parseNumberString(raw: string): number | null {
  const trimmed = raw.trim();
  const paren = trimmed.match(PAREN_NUMBER);
  if (paren) {
    const amount = toNumber(paren[1] ?? '');
    return amount === null ? null : -amount;
  }
  if (!PLAIN_NUMBER.test(trimmed) || isLeadingZeroIdentifier(trimmed)) {
    return null;
  }
  return toNumber(trimmed);
}

export function isEmptyExportValue(raw: unknown): boolean {
  if (raw === null || raw === undefined) {
    return true;
  }
  if (typeof raw !== 'string') {
    return false;
  }
  const trimmed = raw.trim();
  return trimmed === '' || trimmed === '-';
}

export function parseRebateExportCell(raw: unknown): RebateExportCell {
  if (isEmptyExportValue(raw)) {
    return { value: null, kind: 'empty' };
  }
  if (raw instanceof Date && !Number.isNaN(raw.getTime())) {
    return { value: raw, kind: 'date' };
  }
  if (typeof raw === 'number' && !Number.isNaN(raw)) {
    return { value: raw, kind: 'number' };
  }
  if (typeof raw !== 'string') {
    return { value: String(raw), kind: 'text' };
  }
  const currency = parseCurrencyString(raw);
  if (currency !== null) {
    return { value: currency, kind: 'currency' };
  }
  const date = parseDateString(raw);
  if (date) {
    return { value: date, kind: 'date' };
  }
  const number = parseNumberString(raw);
  if (number !== null) {
    return { value: number, kind: 'number' };
  }
  return { value: raw, kind: 'text' };
}
